import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { apiService } from '@/services/api'
import { ProfileData, CSProfileData, BestSellingProduct, ShiftReport } from '@/types'
import { buildEndpoint } from '@/utils/apiUtils'

// Flutter-style Profile Controller - exact match
interface ProfileState {
  // Loading state
  isLoading: boolean
  
  // Profile data
  profileData: ProfileData | null
  
  // CS Profile data
  csProfileData: CSProfileData | null
  
  // متغير لتتبع اللغة الحالية
  currentLanguage: string
  
  // Date filter variables
  selectedDateRange: { startDate: Date | null; endDate: Date | null } | null
}

const initialState: ProfileState = {
  isLoading: true,
  profileData: null,
  csProfileData: null,
  currentLanguage: 'ar',
  selectedDateRange: null,
}

// Flutter-style async thunks matching exact controller methods
export const loadProfileData = createAsyncThunk(
  'profile/loadProfileData',
  async (params: { startDate?: string; endDate?: string }, { getState }) => {
    try {
      const state = getState() as RootState
      
      // Check if user is authenticated first
      if (!apiService.isAuthenticated) {
        throw new Error('User not authenticated')
      }

      // بناء URL مع معاملات التاريخ إذا كانت متوفرة
      // الحالات المدعومة:
      // 1. يوم واحد: ?start_date=2025-08-13
      // 2. نطاق تاريخ: ?start_date=2025-08-13&end_date=2025-08-15
      let url = buildEndpoint('/cashier/profile')
      if (params.startDate && params.endDate) {
        if (params.startDate === params.endDate) {
          // إذا كان نفس اليوم، أرسل start_date فقط
          url += `?start_date=${params.startDate}`
        } else {
          // إذا كان نطاق تاريخ، أرسل كلا التاريخين
          url += `?start_date=${params.startDate}&end_date=${params.endDate}`
        }
      } else if (params.startDate) {
        // إذا كان هناك تاريخ بداية فقط، أرسله
        url += `?start_date=${params.startDate}`
      }

      const response = await apiService.axios.get(url)

      if (response.status === 200) {
        // تمرير البيانات الصحيحة إلى ProfileData أو CSProfileData
        const dataSection = response.data.data as Record<string, any> | undefined
        if (dataSection) {
          if (isCustomerService(state)) {
            const csProfileData = dataSection as CSProfileData
            return { profileData: null, csProfileData }
          } else {
            const profileData = dataSection as ProfileData
            return { profileData, csProfileData: null }
          }
        } else {
          if (isCustomerService(state)) {
            const csProfileData = response.data as CSProfileData
            return { profileData: null, csProfileData }
          } else {
            const profileData = response.data as ProfileData
            return { profileData, csProfileData: null }
          }
        }
      } else if (response.status === 401) {
        // Token expired or invalid
        throw new Error('Authentication error')
      } else {
        throw new Error(`Failed to load profile data: ${response.statusText}`)
      }
    } catch (error) {
      // Check if it's an authentication error
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = (error as Error).message
        if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
          throw new Error('Authentication error')
        }
      }
      throw error
    }
  }
)

export const refreshProfile = createAsyncThunk(
  'profile/refreshProfile',
  async (_, { dispatch }) => {
    return dispatch(loadProfileData({}))
  }
)

export const loadProfileDataWithFilter = createAsyncThunk(
  'profile/loadProfileDataWithFilter',
  async (params: { startDate: string; endDate: string }, { dispatch }) => {
    return dispatch(loadProfileData(params))
  }
)

// Flutter-style Profile Slice - exact match
const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    // Flutter-style reducers matching exact controller methods
    setCurrentLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload
    },

    applyDateFilter: (state, action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>) => {
      state.selectedDateRange = action.payload
    },

    clearDateFilter: (state) => {
      state.selectedDateRange = null
    },

    forceUpdate: (_state) => {
      // Force UI update method for debugging
      // In Redux, this would trigger a re-render automatically
    },

    handleAuthenticationError: (state) => {
      // Clear invalid token
      apiService.clearAuthToken()
      // Reset profile data
      state.profileData = null
      state.csProfileData = null
      state.isLoading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProfileData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(loadProfileData.fulfilled, (state, action) => {
        state.isLoading = false
        state.profileData = action.payload.profileData
        state.csProfileData = action.payload.csProfileData
      })
      .addCase(loadProfileData.rejected, (state, action) => {
        state.isLoading = false
        
        // Check if it's an authentication error
        if (action.error.message?.includes('Authentication error')) {
          state.profileData = null
          state.csProfileData = null
        }
      })
      .addCase(refreshProfile.fulfilled, (_state, _action) => {
        // Refresh completed
      })
      .addCase(loadProfileDataWithFilter.fulfilled, (_state, _action) => {
        // Load with filter completed
      })
  },
})

// Flutter-style helper functions - exact match
function isCustomerService(_state: RootState): boolean {
  try {
    const userData = apiService.getUserData()
    if (userData) {
      const userType = userData.type ?? userData.user_type ?? ''
      return userType.toLowerCase().includes('customer_service')
    }
    return false
  } catch (e) {
    return false
  }
}

// Flutter-style selectors - exact match
export const selectIsLoading = (state: RootState) => state.profile.isLoading
export const selectProfileData = (state: RootState) => state.profile.profileData
export const selectCsProfileData = (state: RootState) => state.profile.csProfileData
export const selectCurrentLanguage = (state: RootState) => state.profile.currentLanguage
export const selectSelectedDateRange = (state: RootState) => state.profile.selectedDateRange

export const selectEmployeeName = (state: RootState) => {
  try {
    // First try to get from API data
    if (state.profile.profileData?.employeeName && state.profile.profileData.employeeName.length > 0) {
      return state.profile.profileData.employeeName
    }

    // Fallback to stored user data from login
    const userData = apiService.getUserData()
    if (userData) {
      const name = userData.name ?? userData.employee_name ?? userData.full_name
      if (name && name.toString().length > 0) {
        return name.toString()
      }
    }

    // Final fallback using translation
    return 'employee_service' // Note: In a real implementation, you'd use i18n
  } catch (e) {
    return 'employee_service'
  }
}

export const selectTotalSales = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.totalSales ?? '0'
  }
  return state.profile.profileData?.totalSales ?? '0'
}

export const selectTotalOrders = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.totalOrders ?? 0
  }
  return state.profile.profileData?.totalOrders ?? 0
}

export const selectTotalProducts = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.totalProducts ?? 0
  }
  return state.profile.profileData?.totalProducts ?? 0
}

export const selectCashSales = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.cashSales ?? '0'
  }
  return state.profile.profileData?.cashSales ?? '0'
}

export const selectCashOrdersCount = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.cashOrdersCount ?? 0
  }
  return state.profile.profileData?.cashOrdersCount ?? 0
}

export const selectNetworkSales = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.onlineSales ?? '0'
  }
  return state.profile.profileData?.networkSales ?? '0'
}

export const selectNetworkOrdersCount = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.onlineOrdersCount ?? 0
  }
  return state.profile.profileData?.networkOrdersCount ?? 0
}

export const selectPartialSales = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.walletSales ?? '0'
  }
  return state.profile.profileData?.partialSales ?? '0'
}

export const selectPartialOrdersCount = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.walletOrdersCount ?? 0
  }
  return state.profile.profileData?.partialOrdersCount ?? 0
}

export const selectBestSellingProducts = (state: RootState): BestSellingProduct[] => {
  if (isCustomerService(state)) {
    // تحويل CSBestSellingProduct إلى BestSellingProduct
    return state.profile.csProfileData?.bestSellingProducts
            .map(
              (csProduct) => ({
                productName: csProduct.productName,
                productImage: csProduct.productImage,
                totalQuantity: csProduct.totalQuantity,
              })
            ) ?? []
  }
  return state.profile.profileData?.bestSellingProducts ?? []
}

export const selectShifts = (state: RootState): ShiftReport[] => {
  if (isCustomerService(state)) {
    // Customer Service لا يحتوي على shifts
    return []
  }
  return state.profile.profileData?.shifts ?? []
}

// CS specific selectors
export const selectAssignedBranches = (state: RootState): string[] => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.assignedBranches ?? []
  }
  return []
}

export const selectEmployeeType = (state: RootState) => {
  if (isCustomerService(state)) {
    return state.profile.csProfileData?.employeeType ?? 'employee'
  }
  return 'cashier'
}

export const selectIsCustomerService = (state: RootState) => {
  return isCustomerService(state)
}

export const selectRecentShifts = (state: RootState): ShiftReport[] => {
  const shifts = selectShifts(state)
  if (shifts.length === 0) return []

  // Sort by start date (most recent first) and take first 10
  const sortedShifts = [...shifts]
  sortedShifts.sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
  )

  return sortedShifts.slice(0, 10)
}

// Flutter-style utility functions
export const formatNumber = (number: any): string => {
  if (number == null) return '0'

  let value: number
  if (typeof number === 'string') {
    value = parseFloat(number) || 0
  } else if (typeof number === 'number') {
    value = number
  } else {
    value = 0
  }

  // Note: In a real implementation, you'd use a proper number formatter
  return value.toLocaleString('ar')
}

export const formatShiftDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    // Note: In a real implementation, you'd use a proper date formatter
    return date.toLocaleDateString('ar')
  } catch (e) {
    return dateString // Return original string if parsing fails
  }
}

export const {
  setCurrentLanguage,
  applyDateFilter,
  clearDateFilter,
  forceUpdate,
  handleAuthenticationError,
} = profileSlice.actions

export default profileSlice.reducer